import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { flare } from "@flareapp/js";
import { Button, Form, Input, InputNumber, Select } from "antd";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const onFinishFailed = (errorInfo) => {
  // eslint-disable-next-line no-console
  console.log("Failed:", errorInfo);
};

const SiteLocationForm = ({ mode, data = {} }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [Admins, setAdmins] = useState([]);
  const [Installers, setInstallers] = useState([]);
  const [businessesList, setBusinessesList] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [isLimitOver, setIsLimitOver] = useState(false);
  const [warnMsg, setWarnMsg] = useState("");

  const getBusiness = async () => {
    setIsLoading(true);
    try {
      const data = await api.get(endpoint.business);
      setBusinessesList(data?.data);
      if (Array.isArray(data.data) && data.data?.length === 1) {
        form.setFieldsValue({ businesses: data?.data[0]?._id });
        onBusinessChange(data?.data[0]?._id);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("business list", error);
    }
    setIsLoading(false);
  };

  const getAdmins = async (business) => {
    try {
      const response = await axios.get(`/user/role/admin`, {
        withCredentials: true,
        params: { business, unknown: true },
      });
      if (response) {
        setAdmins(response?.data);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("admin list", error);
    }
  };

  const getInstaller = async (business) => {
    try {
      const response = await axios.get(`/user/role/installer`, {
        withCredentials: true,
        params: { business, unknown: true },
      });
      if (response) {
        setInstallers(response?.data);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("installer list", error);
    }
  };

  useEffect(() => {
    if (mode === "edit") {
      document.title = `${appConfig.name} - Edit Site Location`;
    } else {
      document.title = `${appConfig.name} - Add A Site Location`;
    }
  }, [mode]);

  const getMySubscription = async () => {
    try {
      const { hasAccess, assignedPlan } = await api.get(endpoint.mySubscription, {
        params: {
          type: "siteLocation",
          unknown: true,
        },
      });
      if (mode !== "edit") {
        setIsLimitOver(!hasAccess);
        if (!hasAccess) {
          setWarnMsg(
            `${assignedPlan?.business?.name}(${assignedPlan?.business?.id})` +
              " has exceeded the limit for the maximum number of Site Location.",
          );
        } else {
          setWarnMsg("");
        }
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("my subscription", error);
    }
  };

  const onBusinessChange = (businesses) => {
    getInstaller(businesses);
    getAdmins(businesses);
    getMySubscription(businesses);
  };

  useEffect(() => {
    if (data?.businesses) {
      onBusinessChange(data.businesses);
    }
  }, [data?.businesses, businessesList]);

  useEffect(() => {
    getBusiness();
  }, []);

  const handleCreate = async (body) => {
    try {
      const res = await api.post(`/site-location`, body);
      if (res) {
        await Swal.fire({
          title: "Done!",
          text: "Site Location Created Successfully",
          icon: "success",
          timer: 2000,
          confirmButtonText: "OK",
        });
        navigate("/site-locations");
      }
    } catch {
      await Swal.fire({
        title: "",
        text: "Something went wrong",
        icon: "error",
        timer: 2000,
        confirmButtonText: "OK",
      });
    }
  };

  const handleUpdate = async (payload) => {
    const body = {
      ...payload,
      latitude: Number(payload?.latitude),
      longitude: Number(payload?.longitude),
    };
    const res = await api.put(`/site-location/` + data?._id, body).catch(function (error) {
      if (error.response) {
        // if (error?.response?.status === 400 || 500) {
        //   console.log(error);
        // }
        // eslint-disable-next-line no-console
        console.log(error.response.headers);
      }
    });
    if (res) {
      await Swal.fire({
        title: "Done!",
        text: "Site Location Edited Successfully",
        icon: "success",
        timer: 2000,
        confirmButtonText: "OK",
      });
      navigate("/site-locations");
    }
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      if (mode === "edit") {
        handleUpdate(values);
      } else {
        handleCreate(values);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card p-3 m-md-3">
      <div>
        <label className="az-content-label">Manage Site Location</label>
        <span className="d-block py-2">{mode === "edit" ? "Edit " : "Add New "} Site Location</span>
      </div>
      <hr className="mt-0 mb-4" />
      <div className="d-flex justify-content-center">{isLoading && <Spinner animation="border" variant="dark" />}</div>
      <Form
        className="siteForm"
        layout="vertical"
        name="site"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        form={form}
        initialValues={{
          ...data,
          admin: Array.isArray(data?.admin) ? data?.admin?.map((d) => d._id) : [],
          installer: Array.isArray(data?.installer) ? data?.installer?.map((d) => d._id) : [],
        }}
      >
        <Row className="gx-4">
          <Col md={6}>
            <Form.Item name="name" rules={[{ required: true, message: "Enter Site Name" }]} label="Site Name">
              <Input placeholder="Enter Site Name" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="id" label="ID">
              <Input placeholder="Enter Site Location ID" />
            </Form.Item>
          </Col>
          {/* business */}
          <Col md={6}>
            <Form.Item
              name="businesses"
              rules={[{ required: true, message: "Select a Business" }]}
              label="Assign Business"
              extra={
                warnMsg && (
                  <p
                    style={{
                      color: "red",
                      marginBottom: 0,
                      marginTop: "2px",
                      display: warnMsg ? "block" : "none",
                    }}
                  >
                    Warning: {warnMsg}
                  </p>
                )
              }
            >
              <Select
                showSearch={true}
                className="w-100"
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                disabled={businessesList.length === 1}
                onChange={(value) => {
                  onBusinessChange(value);
                  form.setFieldsValue({ admin: undefined, installer: undefined });
                }}
                placeholder="Select Business"
                options={[
                  ...businessesList.map((item) => ({
                    label: `${item.name} (${item.id})`,
                    value: item._id,
                  })),
                ]}
                required
              />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item label="Assign Site Admin" name="admin">
              <Select
                className="w-100"
                mode="multiple"
                maxTagCount={1}
                maxTagTextLength={20}
                showSearch={true}
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                // onChange={(value) => onSelectChange({ admin: value })}
                placeholder="Select Single / Multiple Site Admin"
                options={[
                  ...Admins.map((item) => ({
                    label: item.name,
                    value: item._id,
                  })),
                ]}
              />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item label="Assign Installer" name="installer">
              <Select
                className="w-100"
                mode="multiple"
                maxTagCount={1}
                maxTagTextLength={20}
                showSearch={true}
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                // onChange={(value) => onSelectChange({ installer: value })}
                placeholder="Select Single / Multiple Installer"
                options={[
                  ...Installers.map((item) => ({
                    label: item.name,
                    value: item._id,
                  })),
                ]}
              />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item label="Address Line 1" name="buildingAddress1">
              <Input placeholder="Enter address line 1" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item label="Address Line 2" name="buildingAddress2">
              <Input placeholder="Enter Address Line 2" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item
              name="buildingPostalCode"
              rules={[
                {
                  pattern: /^[0-9]+$/,
                  message: "Please Enter Number Only",
                },
              ]}
              label="Postal Code"
            >
              <Input placeholder="Enter Postcode" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="city" label="City">
              <Input placeholder="Enter City" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="state" label="State">
              <Input placeholder="Enter State" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="country" label="Country">
              <Input placeholder="Enter Country" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="latitude" label="Latitude">
              <InputNumber controls={false} className="w-100" placeholder="Enter Latitude" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="longitude" label="Longitude">
              <InputNumber controls={false} className="w-100" placeholder="Enter Longitude" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="remark" label="Remark">
              <Input placeholder="Enter Remark" />
            </Form.Item>
          </Col>
        </Row>
        <Row className="gx-4 pt-4">
          <h5> Contact Person Information </h5>
          <Col md={6}>
            <Form.Item name="contactPersonName" label="Contact Person Name">
              <Input placeholder="Enter Contact Person Name" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item
              name="contactPersonPhone"
              label="Contact Person Phone"
              rules={[
                {
                  pattern: /^[0-9]+$/,
                  message: "Please Enter Number Only",
                },
              ]}
            >
              <Input
                stringMode={true}
                addonBefore={<>+6</>}
                className="w-100"
                placeholder="Enter Contact Person Phone Number"
              />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item
              name="contactPersonEmail"
              rules={[{ type: "email", message: "Provide a Valid Email" }]}
              label="Contact Person Email"
            >
              <Input placeholder="Enter Contact Person Email" />
            </Form.Item>
          </Col>
        </Row>
        <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
          <Button type="primary" htmlType="submit">
            {mode === "edit" ? "Update" : "Create "} Site Location
          </Button>
          <Button type="primary" className="bg-secondary" onClick={() => navigate(-1)} htmlType="button">
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default SiteLocationForm;

SiteLocationForm.propTypes = {
  mode: PropTypes.string,
  data: PropTypes.object,
};
